import { MotionValue, useMotionValue, useTransform } from 'framer-motion'
import { FC, useEffect } from 'react'

import { Collage } from './styled'
import { useMedia } from 'hooks/useMedia'
import { breakpoints } from 'styles/mq'

const IMAGES = {
  regular: {
    LayerFlower: require('./assets/parallax-layers/Collage_flower.webp'),
    LayerPaper: require('./assets/parallax-layers/Collage_Paper.webp'),
    LayerPottery1: require('./assets/parallax-layers/Collage_Pottery.webp'),
    LayerPottery2: require('./assets/parallax-layers/Collage_pottery 2.webp'),
    LayerRunes: require('./assets/parallax-layers/Collage_Runes.webp'),
    LayerThread: require('./assets/parallax-layers/Collage_Thread.webp'),
  },
  hires: {
    LayerFlower: require('./assets/parallax-layers/hires/Collage_flower.webp'),
    LayerPaper: require('./assets/parallax-layers/hires/Collage_Paper.webp'),
    LayerPottery1: require('./assets/parallax-layers/hires/Collage_Pottery.webp'),
    LayerPottery2: require('./assets/parallax-layers/hires/Collage_pottery 2.webp'),
    LayerRunes: require('./assets/parallax-layers/hires/Collage_Runes.webp'),
    LayerThread: require('./assets/parallax-layers/hires/Collage_Thread.webp'),
  },
}

const TRANSFORMS = {
  small: {
    flower: {
      x: ['-55%', '-55%'],
      y: ['-145%', '-145%'],
      other: { scale: 1.6, top: '50%' },
    },
    paper: {
      x: [`-37%`, '-37%'],
      y: ['115%', '115%'],
      other: { scale: 1.3, top: '50%' },
    },
    pottery1: {
      x: [`-20%`, '-20%'],
      y: ['-32%', '-32%'],
      other: { scale: 1.5, top: '50%', rotate: '-18deg' },
    },
    pottery2: {
      x: [`-8%`, '-8%'],
      y: ['-60%', '-60%'],
      other: { rotateZ: '150deg', scale: 1.7, top: '50%' },
    },
    runes: {
      x: [`60%`, '60%'],
      y: ['-45%', '-45%'],
      other: { scale: 1.2, top: '50%' },
    },
    thread: {
      x: [`-7%`, '-7%'],
      y: ['-15%', '-15%'],
      other: { rotateZ: '70deg', scale: 2.3, top: '50%' },
    },
  },
  large: {
    flower: {
      x: [`1%`, '-3%'],
      y: [`0%`, '-30%'],
      other: {},
    },
    paper: {
      x: [`2%`, '-8%'],
      y: [`0%`, '-40%'],
      other: {},
    },
    pottery1: {
      x: [`5%`, '-5%'],
      y: [`0%`, '-70%'],
      other: {},
    },
    pottery2: {
      x: [`1%`, '-1%'],
      y: [`0%`, '-20%'],
      other: {},
    },
    runes: {
      x: [`0%`, '-5%'],
      y: [`0%`, '-50%'],
      other: {},
    },
    thread: {
      x: [`3%`, '3%'],
      y: [`3.5%`, '-80%'],
      other: { scale: 1.4 },
    },
  },
}

export const ParallaxHeader: FC<{ progress: MotionValue<number> }> = ({
  progress,
}) => {
  const mouse = useMotionValue(0)
  const isPortrait = useMedia(['(orientation: portrait)'], [true], false)
  const mq = isPortrait ? 'small' : 'large'

  const xFlower = useTransform(mouse, [0, 1], TRANSFORMS[mq].flower.x)
  const xPaper = useTransform(mouse, [0, 1], TRANSFORMS[mq].paper.x)
  const xPottery1 = useTransform(mouse, [0, 1], TRANSFORMS[mq].pottery1.x)
  const xPottery2 = useTransform(mouse, [0, 1], TRANSFORMS[mq].pottery2.x)
  const xRunes = useTransform(mouse, [0, 1], TRANSFORMS[mq].runes.x)
  const xThread = useTransform(mouse, [0, 1], TRANSFORMS[mq].thread.x)

  const yFlower = useTransform(progress, [0, 1], TRANSFORMS[mq].flower.y)
  const yPaper = useTransform(progress, [0, 1], TRANSFORMS[mq].paper.y)
  const yPottery1 = useTransform(progress, [0, 1], TRANSFORMS[mq].pottery1.y)
  const yPottery2 = useTransform(progress, [0, 1], TRANSFORMS[mq].pottery2.y)
  const yRunes = useTransform(progress, [0, 1], TRANSFORMS[mq].runes.y)
  const yThread = useTransform(progress, [0, 1], TRANSFORMS[mq].thread.y)

  useEffect(() => {
    const handleMouseMove = (evt: MouseEvent) => {
      const { clientX } = evt
      mouse.set(clientX / document.body.clientWidth)
    }

    document.addEventListener('mousemove', handleMouseMove)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
    }
  }, [])

  return (
    <>
      <picture>
        <source srcSet={IMAGES.hires.LayerFlower} media={breakpoints.large} />
        <Collage
          style={{ x: xFlower, y: yFlower, ...TRANSFORMS[mq].flower.other }}
          src={IMAGES.regular.LayerFlower}
          alt="flower"
        />
      </picture>
      <picture>
        <source srcSet={IMAGES.hires.LayerPaper} media={breakpoints.large} />
        <Collage
          style={{ x: xPaper, y: yPaper, ...TRANSFORMS[mq].paper.other }}
          src={IMAGES.regular.LayerPaper}
          alt="paper"
        />
      </picture>
      <picture>
        <source srcSet={IMAGES.hires.LayerPottery1} media={breakpoints.large} />
        <Collage
          style={{
            x: xPottery1,
            y: yPottery1,
            ...TRANSFORMS[mq].pottery1.other,
          }}
          src={IMAGES.regular.LayerPottery1}
          alt="pottery 1"
        />
      </picture>
      <picture>
        <source srcSet={IMAGES.hires.LayerPottery2} media={breakpoints.large} />
        <Collage
          style={{
            x: xPottery2,
            y: yPottery2,
            ...TRANSFORMS[mq].pottery2.other,
          }}
          src={IMAGES.regular.LayerPottery2}
          alt="pottery 2"
        />
      </picture>
      <picture>
        <source srcSet={IMAGES.hires.LayerRunes} media={breakpoints.large} />
        <Collage
          style={{ x: xRunes, y: yRunes, ...TRANSFORMS[mq].runes.other }}
          src={IMAGES.regular.LayerRunes}
          alt="runes"
        />
      </picture>
      <picture>
        <source srcSet={IMAGES.hires.LayerThread} media={breakpoints.large} />
        <Collage
          style={{
            x: xThread,
            y: yThread,
            ...TRANSFORMS[mq].thread.other,
          }}
          src={IMAGES.regular.LayerThread}
          alt="Анализируй Этно"
        />
      </picture>
    </>
  )
}
